import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import GenericErrorBoundary from 'src/components/ErrorBoundary/GenericErrorBoundary';

import withPlaceholder from './withPlaceholder';

/**
 * Wraps content in an error boundary with a caller-supplied skeleton fallback
 * UI. Surface-agnostic: the only per-surface customisation is the background
 * skeleton supplied via `placeholderComponent`.
 * @param {object} props - Component props.
 * @param {string|null} props.bodyText - Text to display in the placeholder.
 * @param {React.ReactNode} props.children - Child content protected by the local boundary.
 * @param {Function|null} props.onCancelFunc - Invoked when the user cancels the error state.
 *   Return true after clearing the underlying source error so the boundary can reset safely.
 * @param {Function|null} props.onRetryFunc - Invoked when the fallback retry action is used.
 * @param {React.ComponentType} props.placeholderComponent - Skeleton component rendered behind
 *   the error modal for the surface being protected.
 * @param {boolean} props.retryDisabled - Disables the fallback retry action.
 * @returns {React.ReactNode} boundary-wrapped child content.
 */
function PlaceholderErrorBoundary({
  bodyText = null, children = null, onCancelFunc = null, onRetryFunc = null, placeholderComponent,
  retryDisabled = false,
}) {
  const fallbackComponent = useMemo(
    () => withPlaceholder(placeholderComponent, bodyText),
    [placeholderComponent, bodyText],
  );

  return (
    <GenericErrorBoundary
      fallbackComponent={fallbackComponent}
      onCancelFunc={onCancelFunc}
      onRetryFunc={onRetryFunc}
      retryDisabled={retryDisabled}
    >
      {children}
    </GenericErrorBoundary>
  );
}

PlaceholderErrorBoundary.propTypes = {
  bodyText: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onCancelFunc: PropTypes.func,
  onRetryFunc: PropTypes.func,
  placeholderComponent: PropTypes.elementType.isRequired,
  retryDisabled: PropTypes.bool,
};

export default PlaceholderErrorBoundary;
